import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { PlusCircle, Loader2, Plus, X, CheckCircle } from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const CATEGORIES = [
  "Payment Processing",
  "Marketing & Advertising",
  "Fulfillment & Logistics",
  "Customer Service",
  "Analytics & Data",
  "Security & Fraud",
  "Inventory Management",
  "CRM & Sales",
  "HR & Operations",
  "Other"
];

interface FormData {
  product_name: string;
  vendor_name: string;
  vendor_email: string;
  pitch_content: string;
  category: string;
  claims: string[];
}

export default function AddPitchPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    product_name: '',
    vendor_name: '',
    vendor_email: '',
    pitch_content: '',
    category: '',
    claims: []
  });
  const [newClaim, setNewClaim] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (value: string) => {
    setFormData({ ...formData, category: value });
  };

  const addClaim = () => {
    if (newClaim.trim() && formData.claims.length < 5) {
      setFormData({ ...formData, claims: [...formData.claims, newClaim.trim()] });
      setNewClaim('');
    }
  };

  const removeClaim = (index: number) => {
    setFormData({
      ...formData,
      claims: formData.claims.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', getApiUrl('/api/pitches/merchant/manual'), true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.onload = function () {
        setLoading(false);
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            setSuccess(true);
            setTimeout(() => navigate('/merchant/pitches'), 2000);
          } else {
            setError(data.detail || 'Failed to add pitch');
          }
        } catch (e) {
          setError('Invalid server response');
        }
      };

      xhr.onerror = function () {
        setLoading(false);
        setError('Network error');
      };

      xhr.send(JSON.stringify(formData));
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (success) {
    return (
      <MerchantLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-emerald-800 mb-2">Pitch Added!</h2>
            <p className="text-emerald-600">The pitch is now pending evaluation.</p>
          </div>
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout>
      <div className="max-w-2xl mx-auto" data-testid="add-pitch-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-900">Add Pitch Manually</h1>
          <p className="text-stone-600 mt-1">Enter details of a pitch you received directly</p>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-700 text-sm">
                {error}
              </div>
            )}

            {/* Vendor Name */}
            <div className="space-y-2">
              <Label htmlFor="vendor_name" className="text-stone-700">Vendor/Company Name *</Label>
              <Input
                id="vendor_name"
                name="vendor_name"
                placeholder="e.g., Acme Technologies"
                value={formData.vendor_name}
                onChange={handleChange}
                required
                data-testid="vendor-name-input"
                className="h-11"
              />
            </div>

            {/* Vendor Email */}
            <div className="space-y-2">
              <Label htmlFor="vendor_email" className="text-stone-700">Vendor Email (optional)</Label>
              <Input
                id="vendor_email"
                name="vendor_email"
                type="email"
                placeholder="sales@acme.com"
                value={formData.vendor_email}
                onChange={handleChange}
                data-testid="vendor-email-input"
                className="h-11"
              />
            </div>

            {/* Product Name */}
            <div className="space-y-2">
              <Label htmlFor="product_name" className="text-stone-700">Product Name *</Label>
              <Input
                id="product_name"
                name="product_name"
                placeholder="e.g., PaymentPro Gateway"
                value={formData.product_name}
                onChange={handleChange}
                required
                data-testid="product-name-input"
                className="h-11"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-stone-700">Category</Label>
              <Select onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="category-select" className="h-11">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Pitch Content */}
            <div className="space-y-2">
              <Label htmlFor="pitch_content" className="text-stone-700">Pitch Details *</Label>
              <Textarea
                id="pitch_content"
                name="pitch_content"
                placeholder="Enter the pitch content, key selling points, pricing mentioned, etc..."
                value={formData.pitch_content}
                onChange={handleChange}
                required
                rows={6}
                data-testid="pitch-content-input"
                className="resize-none"
              />
              <p className="text-xs text-stone-400">
                Include any relevant details from the pitch email or conversation
              </p>
            </div>

            {/* Claims */}
            <div className="space-y-2">
              <Label className="text-stone-700">Key Claims Made (optional)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., 25% cost reduction"
                  value={newClaim}
                  onChange={(e) => setNewClaim(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addClaim())}
                  data-testid="claim-input"
                  className="h-10"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addClaim}
                  disabled={!newClaim.trim() || formData.claims.length >= 5}
                  className="h-10 px-3"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.claims.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.claims.map((claim, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm"
                    >
                      {claim}
                      <button
                        type="button"
                        onClick={() => removeClaim(i)}
                        className="hover:text-emerald-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="pt-4 border-t border-stone-100">
              <Button
                type="submit"
                disabled={loading || !formData.vendor_name || !formData.product_name || !formData.pitch_content}
                data-testid="submit-pitch-btn"
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Pitch
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </MerchantLayout>
  );
}
